import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OCPP_TRACE, TRACE_ENERGY_KWH } from '../landingContent';

const STEP_DELAY_MS = 700;
const METER_TICKS = 24;
const METER_TICK_MS = 55;

const prefersReducedMotion = () =>
  Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);

/**
 * The hero's thesis: a charging session as the stations and the central system
 * actually negotiate it. The frames are the OCPP 1.6 actions ev-backend
 * handles, revealed in order so the sequence itself carries the meaning.
 */
export const OcppTrace = () => {
  const { t } = useTranslation();
  const reducedMotion = useMemo(prefersReducedMotion, []);

  const [visibleCount, setVisibleCount] = useState(
    reducedMotion ? OCPP_TRACE.length : 1,
  );
  const [energy, setEnergy] = useState(reducedMotion ? TRACE_ENERGY_KWH : 0);

  useEffect(() => {
    if (reducedMotion) return;

    const timers: ReturnType<typeof setTimeout>[] = [];

    OCPP_TRACE.forEach((_, index) => {
      if (index === 0) return;
      timers.push(
        setTimeout(() => setVisibleCount(index + 1), STEP_DELAY_MS * index),
      );
    });

    // The meter climbs only once its own frame is on screen.
    const meterIndex = OCPP_TRACE.findIndex((step) => step.isMeter);
    if (meterIndex > -1) {
      for (let tick = 1; tick <= METER_TICKS; tick += 1) {
        timers.push(
          setTimeout(
            () => setEnergy((TRACE_ENERGY_KWH * tick) / METER_TICKS),
            STEP_DELAY_MS * meterIndex + METER_TICK_MS * tick,
          ),
        );
      }
    }

    return () => timers.forEach(clearTimeout);
  }, [reducedMotion]);

  return (
    <figure className="lp-trace" aria-label={t('landing.hero.traceLabel')}>
      <figcaption className="lp-trace__head">
        <span className="lp-trace__dot" aria-hidden="true" />
        {t('landing.hero.traceHead')}
      </figcaption>

      <ol className="lp-trace__list">
        {OCPP_TRACE.slice(0, visibleCount).map((step) => (
          <li
            key={`${step.time}-${step.action}`}
            className={`lp-trace__row lp-trace__row--${step.direction}`}
          >
            <span className="lp-trace__time">{step.time}</span>
            <span className="lp-trace__arrow" aria-hidden="true">
              {step.direction === 'up' ? '↑' : '↓'}
            </span>
            <span className="lp-trace__action">{step.action}</span>
            <span className="lp-trace__detail">
              {step.isMeter ? `${energy.toFixed(2)} kWh` : step.detail}
            </span>
          </li>
        ))}
      </ol>
    </figure>
  );
};

export default OcppTrace;
